import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function HeroSection() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Modal visibility states for Footer Links
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  
  // Modal visibility state for the 30-Sec video lightbox
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  useEffect(() => {
    // 1. Intercept the footer signal explicitly before any other page routing logic takes over
    if (location.state?.openDemoModal) {
      setIsDemoOpen(true);
      
      // 2. Clear out the history container state so refreshes don't auto-trigger the overlay
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  return (
    <>
      {/* GLOBAL NAVIGATION HEADER */}
      <nav className="border-b border-gray-900/80 bg-[#030712]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          
          {/* Logo Construct */}
          <div onClick={() => navigate('/')} className="flex items-center gap-2.5 cursor-pointer select-none group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#c026d3] to-[#a855f7] flex items-center justify-center shadow-lg shadow-purple-500/20 group-hover:scale-105 transition-transform">
              <span className="text-white text-lg font-bold">⚡</span>
            </div>
            <span className="text-xl font-extrabold tracking-tight text-white">
              Atlas <span className="text-[#a855f7]">AI</span>
            </span>
          </div>

          {/* Expanded Navigation Link Matrix */}
          <div className="hidden md:flex items-center gap-8 text-sm font-semibold text-gray-400">
            <button onClick={() => navigate('/')} className="text-white border-b-2 border-[#a855f7] pb-1 cursor-default">Home</button>
            <button onClick={() => navigate('/features')} className="hover:text-white transition-colors">Features</button>
            <button onClick={() => navigate('/calculator')} className="hover:text-white transition-colors">Calculator</button>
            <button onClick={() => navigate('/media')} className="hover:text-white transition-colors">Media</button>
            <button onClick={() => navigate('/contact')} className="hover:text-white transition-colors">Contact</button>
          </div>
        </div>
      </nav>

      {/* CORE WORKSPACE ENTRY */}
      <main className="flex-grow">
        
        {/* TWO-COLUMN HORIZONTAL HERO LAYOUT */}
        <section className="max-w-7xl mx-auto px-6 pt-24 pb-28">
   <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_0.75fr] gap-14 items-center">
            
            {/* LEFT SIDE — TEXT CONTENT */}
            <div className="text-left space-y-8">

              {/* Line 1: Tagline Badge Indicator */}
              <div className="inline-block">
                <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1e113a] border border-[#c026d3]/30 shadow-inner">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#c026d3] animate-pulse"></span>
                  <span className="text-[11px] md:text-xs font-bold font-mono tracking-wide text-purple-300 uppercase">
                    Now answering 50,000+ calls monthly
                  </span>
                </div>
              </div>

              {/* Heading */}
<div className="max-w-4xl">
  <div className="max-w-4xl"></div>
  <h1 className="text-6xl md:text-[88px] font-black tracking-tighter leading-[0.9] text-white">
    <span className="block">Stop Losing</span>
    <span className="block bg-gradient-to-r from-[#c026d3] via-[#e879f9] to-[#a855f7] bg-clip-text text-transparent filter drop-shadow-[0_4px_20px_rgba(192,38,211,0.15)] py-2">
      $32,000/Month
    </span>
    <span className="block">to Missed Calls</span>
  </h1>
</div>

              {/* Paragraph */}
              <p className="text-gray-400 text-lg md:text-xl font-normal leading-relaxed max-w-2xl pt-2">
                Atlas AI responds to every plumbing and electrical lead in 60 seconds—qualifying prospects, booking appointments, and turning your phone into a 24/7 profit center.
              </p>

              {/* CTA Buttons */}
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-start gap-4">
                <button 
                  onClick={() => navigate('/calculator')}
                  className="w-full sm:w-auto h-13 px-8 bg-gradient-to-r from-[#c026d3] to-[#a855f7] hover:opacity-95 active:scale-[0.99] transition-all rounded-xl text-sm font-extrabold tracking-wide text-white"
                >
                  See How Much You're Losing →
                </button>

                <button 
                  onClick={() => setIsDemoOpen(true)}
                  className="border border-gray-800 hover:bg-gray-900/50 text-white rounded-xl px-6 h-12 text-sm font-bold transition-all"
                >
                  Watch 30-Sec Demo
                </button>
              </div>

            </div>

            {/* RIGHT SIDE — AI IMAGE POSITIONED HORIZONTALLY */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative rounded-3xl overflow-hidden border border-purple-500/20 shadow-[0_0_60px_rgba(168,85,247,0.15)]">
                <img
                  src="/ai_19%20prime%203.png"
                  alt="AI Assistant"
                  className="w-full max-w-[620px] object-cover"
                />
                {/* Ambient Visual Glow Overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/10 to-[#c026d3]/10 pointer-events-none" />
              </div>
            </div>

          </div>
        </section>

        {/* CENTERED SOCIAL PROOF SYSTEMS: MULTI-TRADE TESTIMONIAL STACK */}
        <section className="bg-[#070d19]/20 py-24 border-t border-b border-gray-900/40">
          <div className="max-w-4xl mx-auto px-6 space-y-16 text-center">
            
            {/* Testimonial Block 1: Plumbing Focus */}
            <div className="space-y-4">
              <p className="text-xl md:text-2xl font-bold text-white italic tracking-tight leading-relaxed">
                "We went from missing 30 calls/month to capturing 95%.<br className="hidden sm:inline" /> That's $36,000 in recovered revenue."
              </p>
              <p className="text-xs md:text-sm font-extrabold uppercase font-mono tracking-wider text-[#c026d3]">
                — Mike T., Owner, Rapid Response Plumbing
              </p>
            </div>

            {/* Section Divider Asset */}
            <div className="w-12 h-[1px] bg-gray-800/80 mx-auto"></div>

            {/* Testimonial Block 2: Electrical Focus */}
            <div className="space-y-4">
              <p className="text-xl md:text-2xl font-bold text-white italic tracking-tight leading-relaxed">
                "Midnight emergency calls used to wake up my entire family.<br className="hidden sm:inline" /> Now Atlas dispatches trucks flawlessly while I sleep like a baby."
              </p>
              <p className="text-xs md:text-sm font-extrabold uppercase font-mono tracking-wider text-amber-400">
                — David K., Founder, VoltGuard Electrical Systems
              </p>
            </div>

          </div>
        </section>
      </main>

      {/* GLOBAL PRODUCTION FOOTER MODULE */}
      <footer className="bg-[#01040a] border-t border-gray-950 text-left pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-12 gap-10 pb-12">
          
          {/* Left Column: Brand Statement Box */}
          <div className="md:col-span-6 space-y-4">
            <div className="flex items-center gap-2.5 select-none">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#c026d3] to-[#a855f7] flex items-center justify-center shadow-md">
                <span className="text-white text-sm font-bold">⚡</span>
              </div>
              <span className="text-lg font-extrabold tracking-tight text-white">
                Atlas <span className="text-[#a855f7]">AI</span>
              </span>
            </div>
            <p className="text-gray-400 text-sm max-w-sm leading-relaxed">
              The #1 AI phone answering service built specifically for plumbing and electrical businesses. Never miss a lead again.
            </p>
          </div>

          {/* Center Column: Product Link Node */}
          <div className="md:col-span-3 space-y-4">
            <h4 className="text-xs font-extrabold uppercase font-mono tracking-wider text-white">Product</h4>
            <ul className="space-y-2.5 text-sm text-gray-500 font-medium">
              <li><button onClick={() => navigate('/features')} className="hover:text-white transition-colors text-left block">Features</button></li>
              <li><button onClick={() => navigate('/media')} className="hover:text-white transition-colors text-left block">Media</button></li>
            </ul>
          </div>

          {/* Right Column: Company Link Node */}
          <div className="md:col-span-3 space-y-4">
            <h4 className="text-xs font-extrabold uppercase font-mono tracking-wider text-white">Company</h4>
            <ul className="space-y-2.5 text-sm text-gray-500 font-medium">
              <li><button onClick={() => navigate('/contact')} className="hover:text-white transition-colors text-left block">Contact</button></li>
              <li><button onClick={() => setShowAboutModal(true)} className="hover:text-white transition-colors text-left block">About</button></li>
              <li><button onClick={() => setShowPrivacyModal(true)} className="hover:text-white transition-colors text-left block">Privacy Policy</button></li>
            </ul>
          </div>

        </div>

        {/* Bottom Legal Sub-Footer Strip */}
        <div className="max-w-7xl mx-auto px-6 pt-6 border-t border-gray-950 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] font-mono text-gray-600">
          <p>© 2026 Atlas AI. Powered by real AI for trades businesses.</p>
          <div className="flex items-center gap-6">
            <a href="#twitter" className="hover:text-gray-400 transition-colors">Twitter</a>
            <a href="#linkedin" className="hover:text-gray-400 transition-colors">LinkedIn</a>
          </div>
        </div>
      </footer>

      {/* ABOUT POPUP */}
      {showAboutModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-[#0a101f] border border-gray-800 rounded-2xl max-w-md w-full p-6 text-left space-y-4 shadow-2xl relative">
            <h3 className="text-lg font-bold text-white tracking-tight flex items-center gap-2">🚀 About Atlas AI</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Atlas AI is an industry-specialized automation framework engineered exclusively for hard-trade services. We construct reliable voice routing instances and intelligent context interpretation layers that completely handle field dispatch.
            </p>
            <button onClick={() => setShowAboutModal(false)} className="w-full h-11 mt-2 bg-gray-900 rounded-xl text-xs font-bold text-gray-300 transition-colors">Close info Window</button>
          </div>
        </div>
      )}

      {/* PRIVACY POLICY POPUP */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-[#0a101f] border border-gray-800 rounded-2xl max-w-lg w-full p-6 text-left space-y-4 shadow-2xl relative">
            <h3 className="text-lg font-bold text-white tracking-tight flex items-center gap-2">🛡️ Privacy Policy</h3>
            <div className="max-h-[200px] overflow-y-auto pr-1 space-y-3 text-xs text-gray-400 font-mono leading-relaxed">
              <p><strong>Data Minimization:</strong> Atlas AI processes voice metrics and diagnostic summaries exclusively to execute automated schedule updates.</p>
              <p><strong>Security Layering:</strong> Client data is fully encrypted inside isolated databases.</p>
            </div>
            <button onClick={() => setShowPrivacyModal(false)} className="w-full h-11 mt-2 bg-gray-900 rounded-xl text-xs font-bold text-gray-300 transition-colors">Acknowledge & Close</button>
          </div>
        </div>
      )}

      {/* AMBIENT LIGHTBOX OVERLAY MODE */}
      {isDemoOpen && (
        <div 
          className="fixed inset-0 bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10 z-[100] animate-fade-in"
          onClick={() => setIsDemoOpen(false)}
        >
          <div 
            className="relative w-full max-w-4xl bg-[#030712] border border-gray-900 rounded-2xl overflow-hidden shadow-2xl shadow-purple-950/20"
            onClick={(e) => e.stopPropagation()}
          >
            
            {/* Top Close Control Interface */}
            <div className="absolute top-4 right-4 z-50">
              <button 
                onClick={() => setIsDemoOpen(false)}
                className="w-9 h-9 rounded-full bg-black/70 text-gray-400 hover:text-white flex items-center justify-center border border-gray-800/80 hover:border-gray-700 text-xs font-mono transition-all hover:scale-105"
                title="Close Demo"
              >
                ✕
              </button>
            </div>

            {/* ISOLATED ASSET PLAYER BLOCK */}
            <div className="w-full aspect-video bg-black relative flex items-center justify-center">
              <video 
                src="https://atlas-video.b-cdn.net/Atlas_AI%20Simplify%20Your%20Front%20Desk.mp4" 
                controls 
                autoPlay 
                preload="auto"
                className="w-full h-full object-cover"
              >
                Your browser does not support high-fidelity video playbacks.
              </video>
            </div>

            {/* BOTTOM CONTEXT ACCENT BAR */}
            <div className="bg-[#050914] px-6 py-4 border-t border-gray-900/60 flex items-center justify-between text-left">
              <div>
                <h3 className="text-sm font-bold text-white tracking-tight">Atlas AI Overview Blueprint</h3>
                <p className="text-[11px] font-mono text-gray-500">Duration: 30 Seconds • Front Desk Optimization</p>
              </div>
              <button
                onClick={() => {
                  setIsDemoOpen(false);
                  navigate('/calculator');
                }}
                className="px-4 py-2 bg-purple-600/10 border border-purple-500/20 hover:bg-purple-600/20 text-[#a855f7] rounded-lg text-xs font-bold transition-all"
              >
                Launch Growth Calculator
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}